import React, { useState } from 'react';
import { 
  Typography, 
  Card, 
  Table, 
  Badge, 
  Space, 
  Button, 
  Form,
  Row, 
  Col, 
  Input,
  Select,
  Radio,
  DatePicker,
  Modal,
  Breadcrumb,
  message,
  Divider,
} from 'antd';
import { 
  SearchOutlined, 
  UpOutlined, 
  DownOutlined, 
  ReloadOutlined, 
  DownloadOutlined,
  EyeOutlined,
  LockOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

// 定义异常订单数据接口
interface AbnormalOrderData {
  id: string;
  orderNo: string;
  orderAmount: number;
  orderDate: string;
  customer: {
    name: string;
    phone: string;
  };
  employee: {
    name: string;
    id: string;
  };
  store: string;
  status: '待处理' | '已冻结' | '已恢复';
  reason: string;
  exceptionType: '刷单' | '套现' | '虚假交易' | '其他';
  processDate?: string;
  processReason?: string;
  processBy?: string;
}

const ExceptionOrderManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [searchCollapsed, setSearchCollapsed] = useState(false);
  const [actionType, setActionType] = useState<'freeze' | 'recover' | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<AbnormalOrderData | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [viewModalVisible, setViewModalVisible] = useState(false);
  
  // 模拟异常订单数据
  const mockAbnormalOrders: AbnormalOrderData[] = [
    {
      id: '1',
      orderNo: 'ORD2023110100001',
      orderAmount: 3200,
      orderDate: '2023-11-01',
      customer: {
        name: '王小明',
        phone: '138****1234'
      },
      employee: {
        name: '李服务',
        id: 'EMP001'
      },
      store: '北京朝阳店',
      status: '待处理',
      reason: '短时间内多次消费，订单金额异常',
      exceptionType: '刷单',
    },
    {
      id: '2',
      orderNo: 'ORD2023110200002',
      orderAmount: 5800,
      orderDate: '2023-11-02',
      customer: {
        name: '张三',
        phone: '139****5678'
      },
      employee: {
        name: '王助理',
        id: 'EMP002'
      },
      store: '上海浦东店',
      status: '已冻结',
      reason: '订单与顾客消费习惯不符',
      exceptionType: '套现',
      processDate: '2023-11-03',
      processReason: '经查实，存在套现行为',
      processBy: '系统管理员',
    },
    {
      id: '3',
      orderNo: 'ORD2023110300003',
      orderAmount: 4200,
      orderDate: '2023-11-03',
      customer: {
        name: '李四',
        phone: '137****9012'
      },
      employee: {
        name: '赵经理',
        id: 'EMP003'
      },
      store: '广州天河店',
      status: '已恢复',
      reason: '疑似虚假交易',
      exceptionType: '虚假交易',
      processDate: '2023-11-04',
      processReason: '核实为正常交易',
      processBy: '系统管理员',
    },
    {
      id: '4',
      orderNo: 'ORD2023110400004',
      orderAmount: 2800,
      orderDate: '2023-11-04',
      customer: {
        name: '陈五',
        phone: '135****3456'
      },
      employee: {
        name: '黄顾问',
        id: 'EMP004'
      },
      store: '深圳南山店',
      status: '待处理',
      reason: '异地登录下单',
      exceptionType: '其他',
    },
  ];

  // 定义列配置
  const columns: ColumnsType<AbnormalOrderData> = [
    {
      title: '订单编号',
      dataIndex: 'orderNo',
      key: 'orderNo',
    },
    {
      title: '订单金额',
      dataIndex: 'orderAmount',
      key: 'orderAmount',
      render: (amount) => `¥${amount.toFixed(2)}`,
    },
    {
      title: '下单日期',
      dataIndex: 'orderDate',
      key: 'orderDate',
    },
    {
      title: '顾客姓名',
      dataIndex: ['customer', 'name'],
      key: 'customerName',
    },
    {
      title: '顾客手机',
      dataIndex: ['customer', 'phone'],
      key: 'customerPhone',
    },
    {
      title: '员工姓名',
      dataIndex: ['employee', 'name'],
      key: 'employeeName',
    },
    {
      title: '所属门店',
      dataIndex: 'store',
      key: 'store',
    },
    {
      title: '异常类型',
      dataIndex: 'exceptionType',
      key: 'exceptionType',
      render: (type) => {
        const colorMap: Record<string, string> = {
          '刷单': 'red',
          '套现': 'orange',
          '虚假交易': 'volcano',
          '其他': 'blue',
        };
        return <Badge color={colorMap[type]} text={type} />;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap: Record<string, any> = {
          '待处理': 'processing',
          '已冻结': 'error',
          '已恢复': 'success',
        };
        return <Badge status={statusMap[status] as "processing" | "error" | "success"} text={status} />;
      },
    },
    {
      title: '处理日期',
      dataIndex: 'processDate',
      key: 'processDate',
      render: (date) => date || '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            icon={<EyeOutlined />} 
            size="small" 
            type="text"
            onClick={() => handleView(record)}
          >
            查看
          </Button>
          {record.status === '待处理' && (
            <Button 
              icon={<LockOutlined />} 
              size="small" 
              type="text" 
              danger
              onClick={() => handleActionClick('freeze', record)}
            >
              冻结
            </Button>
          )}
          {record.status === '已冻结' && (
            <Button 
              icon={<UnlockOutlined />} 
              size="small" 
              type="text"
              onClick={() => handleActionClick('recover', record)}
            >
              恢复
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // 处理查看按钮点击
  const handleView = (record: AbnormalOrderData) => {
    setCurrentOrder(record);
    setViewModalVisible(true);
  };

  // 处理操作按钮点击
  const handleActionClick = (action: 'freeze' | 'recover', record: AbnormalOrderData) => {
    setActionType(action);
    setCurrentOrder(record);
    setActionReason('');
    setModalVisible(true);
  };

  // 处理操作确认
  const handleActionConfirm = () => {
    if (!actionReason || !currentOrder || !actionType) {
      message.error('请输入操作原因');
      return;
    }

    // 模拟处理操作
    message.success(`已${actionType === 'freeze' ? '冻结' : '恢复'}订单：${currentOrder.orderNo}`);
    
    // 在实际应用中，这里会调用API更新订单状态

    setModalVisible(false);
    setActionReason('');
    setCurrentOrder(null);
    setActionType(null);
  };

  // 处理搜索
  const handleSearch = (values: any) => {
    console.log('Search values:', values);
    // 实际应用中需调用API进行搜索
    message.success('搜索请求已发送');
  };

  // 处理重置
  const handleReset = () => {
    form.resetFields();
  };

  // 渲染搜索表单
  const renderSearchForm = () => (
    <Card style={{ marginBottom: 16 }}>
      <Form form={form} onFinish={handleSearch} layout="vertical">
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="订单编号" name="orderNo">
              <Input placeholder="请输入订单编号" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="顾客姓名" name="customerName">
              <Input placeholder="请输入顾客姓名" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="顾客手机" name="customerPhone">
              <Input placeholder="请输入顾客手机号" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="员工姓名" name="employeeName">
              <Input placeholder="请输入员工姓名" />
            </Form.Item>
          </Col>
        </Row>
        {!searchCollapsed && (
          <>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="门店" name="store">
                  <Select placeholder="请选择门店">
                    <Select.Option value="北京朝阳店">北京朝阳店</Select.Option>
                    <Select.Option value="上海浦东店">上海浦东店</Select.Option>
                    <Select.Option value="广州天河店">广州天河店</Select.Option>
                    <Select.Option value="深圳南山店">深圳南山店</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="异常类型" name="exceptionType">
                  <Select placeholder="请选择异常类型">
                    <Select.Option value="刷单">刷单</Select.Option>
                    <Select.Option value="套现">套现</Select.Option>
                    <Select.Option value="虚假交易">虚假交易</Select.Option>
                    <Select.Option value="其他">其他</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="状态" name="status">
                  <Radio.Group>
                    <Radio.Button value="all">全部</Radio.Button>
                    <Radio.Button value="pending">待处理</Radio.Button>
                    <Radio.Button value="frozen">已冻结</Radio.Button>
                    <Radio.Button value="recovered">已恢复</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="下单日期" name="orderDate">
                  <RangePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
        <Row>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Space>
              <Button 
                type="link" 
                onClick={() => setSearchCollapsed(!searchCollapsed)}
              >
                {searchCollapsed ? '展开' : '收起'} {searchCollapsed ? <DownOutlined /> : <UpOutlined />}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
              <Button type="primary" icon={<SearchOutlined />} htmlType="submit">搜索</Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Card>
  );

  // 渲染操作模态框
  const renderActionModal = () => (
    <Modal
      title={`${actionType === 'freeze' ? '冻结' : '恢复'}异常订单`}
      open={modalVisible}
      onCancel={() => setModalVisible(false)}
      onOk={handleActionConfirm}
      okText="确认"
      cancelText="取消"
    >
      {currentOrder && (
        <>
          <p><strong>订单编号：</strong>{currentOrder.orderNo}</p>
          <p><strong>异常类型：</strong>{currentOrder.exceptionType}</p>
          <p><strong>异常原因：</strong>{currentOrder.reason}</p>
          <Divider />
          <Form.Item
            label={`${actionType === 'freeze' ? '冻结' : '恢复'}原因`}
            required
            rules={[{ required: true, message: '请输入操作原因' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="请输入操作原因" 
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
            />
          </Form.Item>
        </>
      )}
    </Modal>
  );

  // 渲染详情模态框
  const renderViewModal = () => (
    <Modal
      title="异常订单详情"
      open={viewModalVisible}
      onCancel={() => setViewModalVisible(false)}
      footer={[
        <Button key="close" onClick={() => setViewModalVisible(false)}>
          关闭
        </Button>
      ]}
      width={700}
    >
      {currentOrder && (
        <div>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title="基本信息" bordered={false}>
                <Row gutter={[16, 8]}>
                  <Col span={12}><Text strong>订单编号：</Text>{currentOrder.orderNo}</Col>
                  <Col span={12}><Text strong>订单金额：</Text>¥{currentOrder.orderAmount.toFixed(2)}</Col>
                  <Col span={12}><Text strong>下单日期：</Text>{currentOrder.orderDate}</Col>
                  <Col span={12}><Text strong>所属门店：</Text>{currentOrder.store}</Col>
                </Row>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="顾客信息" bordered={false}>
                <p><Text strong>姓名：</Text>{currentOrder.customer.name}</p>
                <p><Text strong>手机：</Text>{currentOrder.customer.phone}</p>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="员工信息" bordered={false}>
                <p><Text strong>姓名：</Text>{currentOrder.employee.name}</p>
                <p><Text strong>ID：</Text>{currentOrder.employee.id}</p>
              </Card>
            </Col>
            <Col span={24}>
              <Card title="异常信息" bordered={false}>
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <Text strong>异常类型：</Text>
                    <Badge 
                      color={
                        currentOrder.exceptionType === '刷单' ? 'red' : 
                        currentOrder.exceptionType === '套现' ? 'orange' : 
                        currentOrder.exceptionType === '虚假交易' ? 'volcano' : 'blue'
                      } 
                      text={currentOrder.exceptionType} 
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong>状态：</Text>
                    <Badge 
                      status={
                        currentOrder.status === '待处理' ? 'processing' : 
                        currentOrder.status === '已冻结' ? 'error' : 'success'
                      } 
                      text={currentOrder.status} 
                    />
                  </Col>
                  <Col span={24}><Text strong>异常原因：</Text>{currentOrder.reason}</Col>
                </Row>
              </Card>
            </Col>
            {(currentOrder.status === '已冻结' || currentOrder.status === '已恢复') && (
              <Col span={24}>
                <Card title="处理信息" bordered={false}>
                  <Row gutter={[16, 8]}>
                    <Col span={12}><Text strong>处理日期：</Text>{currentOrder.processDate}</Col>
                    <Col span={12}><Text strong>处理人：</Text>{currentOrder.processBy}</Col>
                    <Col span={24}><Text strong>处理原因：</Text>{currentOrder.processReason}</Col>
                  </Row>
                </Card>
              </Col>
            )}
          </Row>
        </div>
      )}
    </Modal>
  );

  return (
    <div>
      <Breadcrumb 
        items={[
          { title: '佣金管理' },
          { title: '异常订单处理' },
        ]}
        style={{ marginBottom: 16 }}
      />
      
      <Title level={4}>异常订单处理</Title>
      <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
        您可以在此页面查看和处理系统检测到的异常订单，包括可疑的刷单、套现和虚假交易等行为。
      </Text>
      
      {renderSearchForm()}
      
      <Card 
        title={
          <Space>
            <span>异常订单列表</span>
            <Badge count={mockAbnormalOrders.filter(o => o.status === '待处理').length} style={{ backgroundColor: '#1890ff' }} />
          </Space>
        }
        extra={
          <Button icon={<DownloadOutlined />} type="primary">导出数据</Button>
        }
      >
        <Table 
          columns={columns} 
          dataSource={mockAbnormalOrders}
          rowKey="id"
          pagination={{ 
            showQuickJumper: true,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `共 ${total} 条记录`
          }}
        />
      </Card>
      
      {renderActionModal()}
      {renderViewModal()}
    </div>
  );
};

export default ExceptionOrderManagement; 